import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { trpc, type RouterOutputs } from '@org/clients/school';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
} from '@org/ui';
import { useTranslation } from 'react-i18next';
import { FlagPill, FLAG_CATEGORIES, type FlagLevel } from '../components/student-flag-badges';

export const Route = createFileRoute('/coordination')({
  component: CoordinationPage,
  validateSearch: (search: Record<string, unknown>) => ({
    teacherId: typeof search.teacherId === 'string' ? search.teacherId : undefined,
  }),
});

type Overview = RouterOutputs['teacher']['coordination']['overview'];
type StudentRow = Overview['students'][number];

const LEVELS: FlagLevel[] = ['red', 'yellow', 'green', 'none'];
const LEVEL_ORDER: Record<FlagLevel, number> = { red: 0, yellow: 1, green: 2, none: 3 };

/** The alert that counts for the coordination: the worse of the automatic one and the teacher's own. */
const worstLevel = (student: StudentRow): FlagLevel => {
  const own = student.teacherFlag?.flag;
  if (student.autoFlag.level === 'red' || own === 'red') return 'red';
  if (student.autoFlag.level === 'yellow' || own === 'yellow') return 'yellow';
  if (student.autoFlag.level === 'green' || own === 'green') return 'green';
  return 'none';
};

function CoordinationPage() {
  const { t } = useTranslation('admin');
  const { data: access, isLoading: isAccessLoading } = trpc.teacher.coordination.access.useQuery();

  if (isAccessLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!access?.allowed) {
    return (
      <Card className="border border-dashed border-rose-200 bg-white/80">
        <CardHeader>
          <CardTitle>{t('teacher.coordination.noAccessTitle')}</CardTitle>
          <CardDescription>{t('teacher.coordination.noAccessDescription')}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return <CoordinationOverview />;
}

function CoordinationOverview() {
  const { t } = useTranslation('admin');
  const [teacherId, setTeacherId] = useState<string>('all');
  const [level, setLevel] = useState<string>('all');
  const [group, setGroup] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  // In-Company students follow a different routine; the coordination leaves them out by default.
  const [hideB2b, setHideB2b] = useState(true);

  const { data, isLoading } = trpc.teacher.coordination.overview.useQuery(
    { teacherId: teacherId !== 'all' ? teacherId : undefined },
    { staleTime: 60_000 },
  );
  const allStudents = data?.students ?? [];
  const students = useMemo(
    () => (hideB2b ? allStudents.filter((student) => student.type !== 'b2b') : allStudents),
    [allStudents, hideB2b],
  );
  const teachers = data?.teachers ?? [];

  const groupOptions = useMemo(() => {
    const all = new Set<string>();
    students.forEach((student) => student.groups.forEach((g) => all.add(g)));
    return Array.from(all).sort();
  }, [students]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return students
      .filter((student) => {
        const worst = worstLevel(student);
        if (!showAll && worst === 'none' && !student.teacherFlag) return false;
        if (level !== 'all' && worst !== level) return false;
        if (group !== 'all' && !student.groups.includes(group)) return false;
        if (term && !student.displayName.toLowerCase().includes(term)) return false;
        return true;
      })
      .sort((a, b) => {
        const byLevel = LEVEL_ORDER[worstLevel(a)] - LEVEL_ORDER[worstLevel(b)];
        if (byLevel !== 0) return byLevel;
        return a.displayName.localeCompare(b.displayName);
      });
  }, [students, level, group, search, showAll]);

  // Per-teacher counts of the students shown (red / yellow / green).
  const byTeacher = useMemo(() => {
    const counts = new Map<string, { name: string; red: number; yellow: number; green: number; total: number }>();
    for (const student of students) {
      const entry = counts.get(student.teacher.teacherId) ?? {
        name: student.teacher.name,
        red: 0,
        yellow: 0,
        green: 0,
        total: 0,
      };
      entry.total += 1;
      const worst = worstLevel(student);
      if (worst === 'red') entry.red += 1;
      else if (worst === 'yellow') entry.yellow += 1;
      else if (worst === 'green') entry.green += 1;
      counts.set(student.teacher.teacherId, entry);
    }
    return Array.from(counts.values()).sort((a, b) => b.red - a.red || b.yellow - a.yellow || a.name.localeCompare(b.name));
  }, [students]);

  const selectClass =
    'min-w-[180px] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#cf2e2e] focus:outline-none';

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">{t('teacher.coordination.eyebrow')}</p>
          <h1 className="text-3xl font-semibold text-gray-900">{t('teacher.coordination.title')}</h1>
          <p className="text-sm text-gray-600">{t('teacher.coordination.description')}</p>
        </div>
        <Badge variant="outline" className="rounded-full border-rose-200 bg-white px-4 py-1 text-[#cf2e2e]">
          {t('teacher.coordination.countBadge', { count: rows.length })}
        </Badge>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-rose-50 bg-white p-4 shadow-sm">
        <FilterField label={t('teacher.coordination.filterTeacher')}>
          <Select value={teacherId} onValueChange={setTeacherId}>
            <SelectTrigger className={selectClass}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('teacher.coordination.filterTeacherAll')}</SelectItem>
              {teachers.map((teacher) => (
                <SelectItem key={teacher.teacherId} value={teacher.teacherId}>
                  {teacher.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label={t('teacher.coordination.filterLevel')}>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className={selectClass}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('teacher.coordination.filterLevelAll')}</SelectItem>
              {LEVELS.map((item) => (
                <SelectItem key={item} value={item}>
                  {t(`teacher.flags.levels.${item}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label={t('teacher.coordination.filterGroup')}>
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger className={selectClass}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('teacher.coordination.filterGroupAll')}</SelectItem>
              {groupOptions.map((item) => (
                <SelectItem key={item} value={item}>
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label={t('teacher.coordination.search')}>
          <Input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('teacher.coordination.search')}
            className="min-w-[200px] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#cf2e2e] focus:outline-none"
          />
        </FilterField>
        <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
          <Switch checked={hideB2b} onCheckedChange={setHideB2b} className="data-[state=checked]:bg-[#cf2e2e]" />
          {t('teacher.coordination.hideB2b')}
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
          <Switch checked={showAll} onCheckedChange={setShowAll} className="data-[state=checked]:bg-[#cf2e2e]" />
          {t('teacher.coordination.showAll')}
        </label>
      </div>

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!isLoading && (
        <Card className="border border-rose-50 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-gray-900">{t('teacher.coordination.byTeacher')}</CardTitle>
            <CardDescription className="text-xs">{t('teacher.coordination.byTeacherHint')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {byTeacher.map((entry) => (
              <button
                type="button"
                key={entry.name}
                onClick={() => {
                  const match = teachers.find((teacher) => teacher.name === entry.name);
                  setTeacherId(match && teacherId !== match.teacherId ? match.teacherId : 'all');
                }}
                className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm text-gray-800 transition hover:border-rose-200 hover:bg-rose-50"
              >
                <span className="font-medium">{entry.name}</span>
                <FlagPill level="red" label={String(entry.red)} />
                <FlagPill level="yellow" label={String(entry.yellow)} />
                <FlagPill level="green" label={String(entry.green)} />
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {!isLoading && rows.length === 0 && (
        <Card className="border border-dashed border-rose-200 bg-white/80">
          <CardHeader>
            <CardTitle>{t('teacher.coordination.empty')}</CardTitle>
          </CardHeader>
        </Card>
      )}

      {!isLoading && rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-rose-50 bg-white shadow-sm">
          <table className="min-w-[1100px] w-full text-sm">
            <thead className="bg-rose-50/50 text-left text-xs uppercase tracking-[0.15em] text-gray-500">
              <tr>
                <th className="px-4 py-3">{t('teacher.coordination.columns.student')}</th>
                <th className="px-3 py-3">{t('teacher.coordination.columns.teacher')}</th>
                <th className="px-3 py-3">{t('teacher.coordination.columns.group')}</th>
                <th className="px-3 py-3">{t('teacher.coordination.columns.auto')}</th>
                <th className="px-3 py-3">{t('teacher.coordination.columns.attendance')}</th>
                <th className="px-3 py-3">{t('teacher.coordination.columns.homework')}</th>
                <th className="px-3 py-3">{t('teacher.coordination.columns.community')}</th>
                <th className="px-3 py-3">{t('teacher.coordination.columns.presence')}</th>
                <th className="px-3 py-3">{t('teacher.coordination.columns.activities')}</th>
                <th className="px-3 py-3">{t('teacher.coordination.columns.teacherFlag')}</th>
                <th className="px-3 py-3">{t('teacher.coordination.columns.comment')}</th>
                <th className="px-3 py-3">{t('teacher.coordination.columns.flaggedOn')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((student) => (
                <tr key={`${student.teacher.teacherId}-${student.studentId}`} className="border-t border-gray-100 align-top">
                  <td className="px-4 py-3">
                    <Link
                      to="/students/$studentId"
                      params={{ studentId: student.studentId }}
                      search={{ teacherId: student.teacher.teacherId }}
                      title={t('teacher.coordination.openProfile')}
                      className="font-medium text-gray-900 hover:text-[#cf2e2e]"
                    >
                      {student.displayName}
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-gray-700">{student.teacher.name}</td>
                  <td className="px-3 py-3 text-gray-700">{student.groups[0] ?? t('teacher.students.noGroup')}</td>
                  <td className="px-3 py-3">
                    <FlagPill level={student.autoFlag.level} />
                  </td>
                  {FLAG_CATEGORIES.map((category) => (
                    <td key={category} className="px-3 py-3">
                      <FlagPill
                        level={student.autoFlag.categories[category].level}
                        title={student.autoFlag.categories[category].reasons
                          .map((reason) => t(`teacher.flags.reasons.${reason.code}`, { value: reason.value, threshold: reason.threshold }))
                          .join('; ')}
                      />
                    </td>
                  ))}
                  <td className="px-3 py-3 text-gray-700">
                    {student.metrics.presence.rate !== null ? `${student.metrics.presence.rate}%` : '—'}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {student.metrics.homework.rate !== null ? `${student.metrics.homework.rate}%` : '—'}
                  </td>
                  <td className="px-3 py-3">
                    {student.teacherFlag ? (
                      <div className="space-y-1">
                        <FlagPill level={student.teacherFlag.flag} />
                        <p className="text-xs text-gray-600">
                          {student.teacherFlag.reason
                            ? t(`teacher.flags.reasonCategories.${student.teacherFlag.reason}`)
                            : t('teacher.flags.noReason')}
                        </p>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">{t('teacher.coordination.noTeacherFlag')}</span>
                    )}
                  </td>
                  <td className="max-w-[260px] px-3 py-3 text-xs text-gray-600">
                    {student.teacherFlag?.comment ?? ''}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    {student.teacherFlag ? new Date(student.teacherFlag.createdAt).toLocaleDateString() : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs uppercase tracking-[0.2em] text-gray-500">{label}</label>
      {children}
    </div>
  );
}
